
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Edit3 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface PractitionerDetailsProps {
  practitioner: any;
  getStatusBadge: (status: string) => React.ReactNode;
  getVerificationBadge: (isVerified: boolean) => React.ReactNode;
  formatDate: (date: string) => string;
  onUpdateStatus: (id: string, status: string) => Promise<void>;
  onUpdateVerification: (id: string, isVerified: boolean) => Promise<void>;
}

const PractitionerDetails: React.FC<PractitionerDetailsProps> = ({
  practitioner,
  getStatusBadge,
  getVerificationBadge,
  formatDate,
  onUpdateStatus,
  onUpdateVerification,
}) => {
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const { toast } = useToast();

  const handleStatusUpdate = async (newStatus: string) => {
    setIsLoading(true);
    try {
      await onUpdateStatus(practitioner.id, newStatus);
      setShowStatusDialog(false);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationUpdate = async (isVerified: boolean) => {
    setIsLoading(true);
    try {
      await onUpdateVerification(practitioner.id, isVerified);
      setShowVerificationDialog(false);
    } catch (error) {
      console.error('Error updating verification:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserEmail = async (userId: string, newEmail: string) => {
    if (!newEmail || newEmail === practitioner.email) return;

    setIsUpdatingEmail(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('update-user-email', {
        body: { userId, newEmail }
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Email Updated Successfully',
        description: `Email has been changed from ${data.oldEmail} to ${data.newEmail}`,
      });

      // Update the practitioner email in the parent component
      practitioner.email = newEmail;
      setIsEmailDialogOpen(false);
      setNewEmail('');
      
    } catch (error) {
      console.error('Error updating user email:', error);
      toast({
        variant: 'destructive',
        title: 'Email Update Failed',
        description: error.message || 'Unable to update user email',
      });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleEmailChange = () => {
    setNewEmail(practitioner.email || '');
    setIsEmailDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Full Name</label>
              <p>{practitioner.first_name} {practitioner.last_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <div className="flex items-center gap-2">
                <span>{practitioner.email}</span>
                <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEmailChange}
                      className="h-6 w-6 p-0"
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Update User Email</DialogTitle>
                      <DialogDescription>
                        Updating email for {practitioner.first_name} {practitioner.last_name}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <label htmlFor="current-email" className="text-right text-sm font-medium">
                          Current Email:
                        </label>
                        <div className="col-span-3 text-sm text-gray-600">
                          {practitioner.email}
                        </div>
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <label htmlFor="new-email" className="text-right text-sm font-medium">
                          New Email:
                        </label>
                        <Input
                          id="new-email"
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="col-span-3"
                          placeholder="Enter new email address"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEmailDialogOpen(false);
                          setNewEmail('');
                        }}
                        disabled={isUpdatingEmail}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => updateUserEmail(practitioner.id, newEmail)}
                        disabled={isUpdatingEmail || !newEmail || newEmail === practitioner.email}
                      >
                        {isUpdatingEmail ? 'Updating...' : 'Update Email'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p>{practitioner.phone_number || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Registration Date</label>
              <p>{formatDate(practitioner.created_at)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Profile Status</span>
              <div className="flex items-center gap-2">
                {getStatusBadge(practitioner.profile_status)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStatusDialog(true)}
                >
                  Update
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Verification Status</span>
              <div className="flex items-center gap-2">
                {getVerificationBadge(practitioner.is_verified)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVerificationDialog(true)}
                >
                  Update
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Professional Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">Bio</label>
            <p className="text-sm">{practitioner.bio || 'No bio provided'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Specializations</label>
            <p className="text-sm">{practitioner.specializations || 'Not specified'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Years of Experience</label>
            <p className="text-sm">{practitioner.years_of_experience || 'Not specified'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Education</label>
            <p className="text-sm">{practitioner.education || 'Not provided'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Practitioner Status</AlertDialogTitle>
            <AlertDialogDescription>
              Choose the new status for this practitioner. This will affect their ability to receive bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-3 gap-2 py-4">
            <Button
              variant={practitioner.profile_status === 'pending_review' ? 'default' : 'outline'}
              onClick={() => handleStatusUpdate('pending_review')}
              disabled={isLoading}
            >
              Pending Review
            </Button>
            <Button
              variant={practitioner.profile_status === 'approved' ? 'default' : 'outline'}
              onClick={() => handleStatusUpdate('approved')}
              disabled={isLoading}
            >
              Approved
            </Button>
            <Button
              variant={practitioner.profile_status === 'rejected' ? 'destructive' : 'outline'}
              onClick={() => handleStatusUpdate('rejected')}
              disabled={isLoading}
            >
              Rejected
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verification Update Dialog */}
      <AlertDialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Verification Status</AlertDialogTitle>
            <AlertDialogDescription>
              Verified practitioners have a verification badge on their profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2 py-4">
            <Button
              variant={practitioner.is_verified ? 'default' : 'outline'}
              onClick={() => handleVerificationUpdate(true)}
              disabled={isLoading}
            >
              Verified
            </Button>
            <Button
              variant={!practitioner.is_verified ? 'default' : 'outline'}
              onClick={() => handleVerificationUpdate(false)}
              disabled={isLoading}
            >
              Unverified
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PractitionerDetails;
